package dev.akshaychordiya.pages.articles

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initBuildComplexScreensFasterOnAndroidPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("articles/BuildComplexScreensFasterOnAndroid.md", FrontMatterElement.Builder { addScalar("title", "Build complex screens faster on Android with Epoxy"); addScalar("date", "2019-12-30"); addScalar("description", "RecyclerView is such an important UI component on Android. But using it can get complex and error prone—especially when dealing with different view types and pagination. Moreover…"); addScalar("originalUrl", "https://medium.com/bleeding-edge/build-complex-screens-faster-on-android-1773c5a64352"); addScalar("originalName", "Medium"); addScalar("image", "/images/articles/build-complex-screens-faster-on-android/1aGPUW5Jlpeo99yq4sRcvqw2x.webp"); }))
}

@Page("/articles/build-complex-screens-faster-on-android")
@Layout(".components.layouts.ArticleLayout")
@Composable
fun BuildComplexScreensFasterOnAndroidPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("/images/articles/build-complex-screens-faster-on-android/1aGPUW5Jlpeo99yq4sRcvqw2x.webp", "")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Rowing the boat — Illustration by ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://www.instagram.com/martapucciofficial/?hl=en") {
                org.jetbrains.compose.web.dom.Text("Marta Pucci")
            }
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("build-complex-screens-faster-on-android-our-journey-with-epoxy") }) {
        org.jetbrains.compose.web.dom.Text("Build complex screens faster on Android⚡ — Our journey with Epoxy")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("RecyclerView is such an important UI component on Android. But using it can get complex and error prone—especially when dealing with different view types and pagination. Moreover, it’s hard when working on the same screen within the team.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In this article, I’m going to share basics of Epoxy and an overview of the problems it helped us solve. Here’s our journey of how we adopted ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/airbnb/epoxy") {
            org.jetbrains.compose.web.dom.Text("Epoxy")
        }
        org.jetbrains.compose.web.dom.Text(" at ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://helloclue.com/") {
            org.jetbrains.compose.web.dom.Text("Clue")
        }
        org.jetbrains.compose.web.dom.Text(" to build our UIs faster in parallel within the team.⚡")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("backstory") }) {
        org.jetbrains.compose.web.dom.Text("Backstory")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It all started with our users, who wanted ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("a more understandable and interactive view")
        }
        org.jetbrains.compose.web.dom.Text(" of their historical tracking data. ✨(For Clue users, this is generally their menstrual cycles & symptoms.)")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So our awesome design team came up with this beautiful intuitive design 😍:")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("/images/articles/build-complex-screens-faster-on-android/1ZWWxjMcnFCxocbiaeCH7PQ.webp", "")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Enhanced Analysis screen in Clue app")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I know what you’re thinking already: that’s a lot of view types. And we were definitely concerned about the performance ️and responsiveness of the screen.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That’s when we decided to see if we could use any third-party library to help us with our goal.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("epoxy") }) {
        org.jetbrains.compose.web.dom.Text("Epoxy")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/airbnb/epoxy") {
            org.jetbrains.compose.web.dom.Text("Epoxy")
        }
        org.jetbrains.compose.web.dom.Text(" is a library from Airbnb which takes a declarative / composable approach to build the UI—typically, a list.")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("It uses RecyclerView under the hood")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("It simplifies creation of static and dynamic layouts")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Each item in the list is associated to an epoxy model which defines the item’s layout and manages binding the data and releasing the view’s resources when it’s recycled.")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Item <-> Epoxy Model <-> Layout resource")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The order of the items shown on the UI is based on how you add the model to the adapter or controller.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("philosophy") }) {
        org.jetbrains.compose.web.dom.Text("Philosophy")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Epoxy encourages a usage pattern similar to the popular MVVM and MVI architecture where the data flows in one direction (UDF - uni-directional data flow).")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("/images/articles/build-complex-screens-faster-on-android/16XomrqB2AloGT2_cvd0ilA.webp", "")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Flow with Epoxy")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The data forms the state of the UI then goes to EpoxyModel and to the views on the RecyclerView .")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The Epoxy Model behaves like a ViewModel and provides the interface between the data and the view. This way the view cannot directly modify the EpoxyModel it gives a callback to modify the state.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("components") }) {
        org.jetbrains.compose.web.dom.Text("Components")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There are 2 key components or building blocks in Epoxy:")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("EpoxyModel")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every item type is represented by an EpoxyModel, which controls binding of data and how to display the view on the UI. These models are mapped to your custom views or xml layouts.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It’s just like ViewModel that is an immutable class which works as an interface between data and the view.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For example, here’s how a model representing a header will look:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("@EpoxyModelClass(layout = R.layout.*header*)\nabstract class HeaderModel : EpoxyModelWithHolder<HeaderModel.ViewHolder>() {\n\n    @EpoxyAttribute\n    lateinit var text: String\n\n    override fun bind(holder: ViewHolder) {\n        holder.textView.*text = *text\n    }\n\n    class ViewHolder : BaseEpoxyHolder() {\n        val textView: TextView by bind(R.id.*header_text*)\n    }\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("EpoxyController / EpoxyAdapter")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("EpoxyController or EpoxyAdapter implementation contains declaration of all the models to show on the RecyclerView.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("class SampleController() : TypedEpoxyController<T>() {\n    override fun buildModels(data: <T>) {\n        header {\n            id(\"averages\")\n            text(\"Your cycle data averages\")\n        }\n    }\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Epoxy uses annotation processing (yes, I know it adds build time—but it’s worth it until they move to a different approach, or Jetpack Compose arrives), so we can leverage DSL style pattern to create our models.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Without DSL style:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("override fun buildModels(data: <T>) {\n    HeaderModel_() {\n        .id(\"averages\")\n        .text(\"Your cycle data averages\")\n        .addTo(this);\n    }\n}\n") } }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Tip ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("💡 —")
            }
            org.jetbrains.compose.web.dom.Text(" You can subclass EpoxyController but I prefer TypedEpoxyController since it adds more type safety")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("why-epoxy") }) {
        org.jetbrains.compose.web.dom.Text("Why Epoxy?")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We didn’t compare all the libraries out there, we instantly liked Epoxy mainly due to 3 key things:")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The number of stars 🌟 to the repository and active development")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Detailed documentation")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Syntax and Kotlin support")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("our-journey") }) {
        org.jetbrains.compose.web.dom.Text("Our journey")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Most of the advantages which we leveraged were because of the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("loose coupling")
        }
        org.jetbrains.compose.web.dom.Text(" between the view types and Epoxy model.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("working-in-a-team") }) {
        org.jetbrains.compose.web.dom.Text("Working in a team 👨‍💻🔀👩‍💻")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Generally with Epoxy each view type is associated with it’s own model class and layout resource which reduces the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("coupling")
        }
        org.jetbrains.compose.web.dom.Text(" with other view types and increases ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("reusability ⬆️")
        }
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("View Type <-> Epoxy Model <-> Layout resource")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This makes it super easy for every team member to build the model and its layout ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("independently,")
        }
        org.jetbrains.compose.web.dom.Text(" hence in parallel.")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("For example,")
            org.jetbrains.compose.web.dom.Br()
            org.jetbrains.compose.web.dom.Text("One member of the team works on the header model and other one works on cycle stats model, making it easier and faster to divide the work among the team.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("evolution-of-ui") }) {
        org.jetbrains.compose.web.dom.Text("Evolution of UI 🧬")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("All the models and their respective layouts are like building blocks, and they have super low coupling—which made it easy to built our UI iteratively. That is: we kept adding new features and fine tuning the UI based on user feedback. Having separate models helped us again to work in parallel and build the UI in blocks.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The side effect of having modular design made it easier to do updates only to the necessary piece, making it easier for maintenance. 🛠")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("building-the-whole-screen-in-blocks") }) {
        org.jetbrains.compose.web.dom.Text("Building the whole screen in blocks 🧱")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Epoxy doesn’t just help building a list of items. It can help ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("compose")
        }
        org.jetbrains.compose.web.dom.Text(" an entire screen.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We built a whole new screen by leveraging Epoxy and the models which normally would be made with a ScrollView and all the basic UI elements, which makes it hard to maintain and evolve over time since it’s less reusable overall.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("/images/articles/build-complex-screens-faster-on-android/1AEXD6aaKA2eYJ-MUWpQitQ.webp", "")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Symptom details screen in Clue app")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("All the UI items represented by blue outline in the screenshot are ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("composed")
        }
        org.jetbrains.compose.web.dom.Text(" together using various different models to hash out the final UI.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("little-things") }) {
        org.jetbrains.compose.web.dom.Text("Little things 🐣")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Epoxy provides little things which turn out to be super useful, like:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("It sets LinearLayoutManager by default [before, I always used to forget to do that and then keep wondering why my RecyclerView is not showing anything 🤔]")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Sets clipToPadding to false by default to prevent showing top padding while scrolling")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Declarative style which makes it easier to visualize the UI; to me this feels a bit like ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://developer.android.com/jetpack/compose") {
                org.jetbrains.compose.web.dom.Text("Jetpack Compose")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Supports automatic diffing + background diffing (optional)")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Pretty solid documentation and issue tracker")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("These little things save a lot of time and make a ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("huge difference to the development experience")
        }
        org.jetbrains.compose.web.dom.Text(" in the end.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("performance") }) {
        org.jetbrains.compose.web.dom.Text("Performance 💯")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Epoxy is mainly an abstraction on top of RecyclerView with lots of features out of the box, so ideally you should expect the same performance as using RecyclerView without any overhead.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Just make sure to follow the same rules while using RecyclerView and tweak the configuration of “")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/airbnb/epoxy/wiki/Configuration#controlling-the-options") {
            org.jetbrains.compose.web.dom.Text("Validating the Epoxy model")
        }
        org.jetbrains.compose.web.dom.Text("” in production as mentioned in the wiki to squeeze out more performance. 🏋️‍♂️")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("the-challenges") }) {
        org.jetbrains.compose.web.dom.Text("The challenges")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Our team faced couple of challenges when adopting Epoxy:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The learning curve isn’t steep, but there is still a learning curve to use Epoxy which can consume a bit of time especially for new joiners in the team. 📈")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Stack overflow driven-development isn’t very useful for Epoxy as compared to RecyclerView, so you might need to rely on documentation and understanding the internals of Epoxy to figure out any issues or do something complex, though the ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/airbnb/epoxy/issues") {
                org.jetbrains.compose.web.dom.Text("issue tracker")
            }
            org.jetbrains.compose.web.dom.Text(" help sometimes")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The number of resources and libraries available for RecyclerView are obviously more than Epoxy, which can be an obstacle sometimes. But the silver lining is you can use most of stuff for Epoxy, since it’s more or less an abstraction on RecyclerView .")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("We showed our journey to iOS devs. They wanted to have something similar, but it’s not available for iOS. 🍎 Airbnb has created one for iOS, but it’s not open sourced.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("conclusion") }) {
        org.jetbrains.compose.web.dom.Text("Conclusion")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I hope this gives an idea of what Epoxy is like and helps you to understand the possibilities. I also hope our journey makes it easier for you and/or your team to evaluate whether to use it. 🛣")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I’d still recommend to spin a sample project with Epoxy to have a first-hand experience and make the final decision.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Our experience in a nutshell 🥜:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("easy to manage different view types")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("parallelizing within the team")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("meeting complex user design demands like sticky headers, pagination, and more")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("easier maintenance")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("happy developer experience 😇")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-s-next") }) {
        org.jetbrains.compose.web.dom.Text("What’s next?")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In future articles, I will:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Show how to build an entire screen step by step using Epoxy")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Show how to do really cool and complex things like sync scrolling or sticky headers with Epoxy")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Deep dive into internals of Epoxy")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://medium.com/bleeding-edge/build-complex-screens-faster-on-android-1773c5a64352") {
            org.jetbrains.compose.web.dom.Text("Build complex screens faster on Android with Epoxy")
        }
        org.jetbrains.compose.web.dom.Text(" was originally published in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://medium.com/bleeding-edge") {
            org.jetbrains.compose.web.dom.Text("Bleeding Edge")
        }
        org.jetbrains.compose.web.dom.Text(" on Medium, where people are continuing the conversation by highlighting and responding to this story.")
    }
}
